import {
  type JobWithRelations,
  type Project,
  type ProjectCopilotConfiguration,
  ScreeningAction,
  ScreeningType,
} from '@factoryfixinc/ats-interfaces';
import type { DynamicScreeningQuestion } from '@factoryfixinc/ats-interfaces';

import { ErrorService } from '@/core/shared/errors/error.service';
import ProjectCopilotConfigurationUtils from '@/utils/project/project-copilot-configuration.utils';

export type ProjectDescriptionJob = JobWithRelations<'screenerQuestions'> & {
  projectCopilotConfiguration?: ProjectCopilotConfiguration;
};

export type ProjectDescriptionScreeningDetails = {
  title: string;
  summary: string;
  note?: string;
  emptyState?: string;
  questionListType?: 'ordered' | 'unordered';
  questions?: string[];
  url?: string;
  urlHref?: string;
};

const STICK_TO_SET_QUESTIONS_NOTE = [
  'Preset questions are only used for screening direct applicants.',
  'If your AI recruiter reaches out to passive candidates, the conversation stays focused on',
  'introducing the role and gauging interest.',
].join(' ');

const INTERVIEW_AND_DIG_DEEPER_SUMMARY = [
  "Your AI recruiter reviews the candidate's resume and asks meaningful questions to get the",
  'full picture - weaving in your guidance questions where they fit naturally.',
].join(' ');

const URL_WITH_PROTOCOL_PATTERN = /^[a-z][a-z\d+\-.]*:\/\//i;

function normalizeExternalUrl(url: string): string {
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return '';
  }

  if (trimmedUrl.startsWith('//')) {
    return `https:${trimmedUrl}`;
  }

  if (URL_WITH_PROTOCOL_PATTERN.test(trimmedUrl)) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
}

function getValidStaticQuestions(questions?: ProjectDescriptionJob['screenerQuestions']): string[] {
  return [...(questions ?? [])]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((question) => question.question?.trim())
    .filter((question): question is string => Boolean(question));
}

function getValidDynamicQuestions(questions?: DynamicScreeningQuestion[]): string[] {
  return [...(questions ?? [])]
    .map((question, index) => ({
      index,
      order: question.order ?? index,
      text: question.text?.trim(),
    }))
    .filter((question): question is { index: number; order: number; text: string } =>
      Boolean(question.text),
    )
    .sort((a, b) => {
      if (a.order === b.order) {
        return a.index - b.index;
      }

      return a.order - b.order;
    })
    .map((question) => question.text);
}

function getProjectCopilotConfiguration(
  job: ProjectDescriptionJob,
  project?: Project,
): ProjectCopilotConfiguration | null | undefined {
  return job.projectCopilotConfiguration ?? project?.projectCopilotConfiguration;
}

export function getProjectDescriptionScreeningDetails(
  job: ProjectDescriptionJob,
  project?: Project,
): ProjectDescriptionScreeningDetails {
  const screeningAction = job.screeningAction ?? ScreeningAction.SCREEN;

  if (screeningAction === ScreeningAction.URL) {
    const employerCareerSiteUrl = job.employerCareerSiteUrl?.trim() ?? '';

    return {
      title: 'Redirect to careers page',
      summary: "Your AI recruiter sends candidates to your company's careers page to apply.",
      url: employerCareerSiteUrl,
      urlHref: normalizeExternalUrl(employerCareerSiteUrl),
    };
  }

  const staticQuestions = getValidStaticQuestions(job.screenerQuestions);

  if (job.screeningType === ScreeningType.DYNAMIC) {
    let dynamicQuestions: string[] = [];

    try {
      const snapshot = ProjectCopilotConfigurationUtils.parseProjectCopilotConfiguration(
        getProjectCopilotConfiguration(job, project),
      );
      dynamicQuestions = getValidDynamicQuestions(snapshot.dynamicScreeningQuestions);
    } catch (error) {
      ErrorService.captureException(error);
      dynamicQuestions = [];
    }

    return {
      title: 'Interview and dig deeper',
      summary: INTERVIEW_AND_DIG_DEEPER_SUMMARY,
      questionListType: 'unordered',
      questions: dynamicQuestions,
      emptyState: dynamicQuestions.length === 0 ? 'No guidance questions added.' : undefined,
    };
  }

  if (staticQuestions.length > 0) {
    return {
      title: 'Stick to set questions',
      summary: 'Your AI recruiter uses your preset questions for candidates who apply directly.',
      note: STICK_TO_SET_QUESTIONS_NOTE,
      questionListType: 'ordered',
      questions: staticQuestions,
    };
  }

  return {
    title: 'Skip automated screening entirely',
    summary: 'Your AI recruiter does not ask candidates any questions at all.',
  };
}
